import type { OfficeSeoServiceKey } from 'ux/Public/OfficeSeo/data/types';

/** Default "client engagement" line per service (same nationwide today). */
export const OFFICE_SEO_DEFAULT_CLIENT_ENGAGEMENT: Record<OfficeSeoServiceKey, string> = {
  cleaning: 'brought Codi in to manage office cleaning on a weekly schedule.',
  pantryRestocking: 'brought Codi in to handle pantry restocking \u2014 it runs on autopilot now.',
  internet: 'brought Codi in to handle IT setup and managed internet from day one.',
  officeAssistant: 'brought Codi in for on-site office management \u2014 no full-time hire needed.',
  officeFurniture: 'brought Codi in to manage their entire office setup and furniture coordination.',
  deepCleaning: 'brought Codi in to schedule quarterly deep cleans \u2014 zero coordination from the team.',
  carpetCleaning: 'brought Codi in to handle carpet and upholstery cleaning on a recurring schedule.',
  windowCleaning: 'brought Codi in to manage window cleaning so the team never has to think about it.',
  glassConferenceRoom: 'brought Codi in to keep the conference room glass spotless \u2014 it runs on autopilot.',
  wasteRecycling: 'brought Codi in to handle waste and recycling hauling so the office stays compliant.',
  pantryInventory:
    'brought Codi in to automate pantry inventory tracking and reordering \u2014 stockouts are a thing of the past.',
  coffeeService: 'brought Codi in to manage office coffee service \u2014 beans, equipment, and maintenance included.',
  waterService: 'brought Codi in to handle filtered water delivery and dispenser maintenance on a set schedule.',
  teamLunch: 'brought Codi in to coordinate recurring team lunches \u2014 one request, consistent delivery.',
  breakfastCatering: 'brought Codi in to run weekly office breakfast so the team starts the day together.',
  individualMeals: 'brought Codi in to manage daily individual meal delivery for the whole office.',
  adHocCatering: 'brought Codi in to handle all office catering requests \u2014 from board meetings to all-hands.',
  happyHour: 'brought Codi in to run recurring office happy hours \u2014 drinks, setup, and cleanup all covered.',
  conferenceRoomAv:
    'brought Codi in to set up and manage conference room A/V \u2014 screens, cameras, and sound all handled.',
  networkInstallation:
    'brought Codi in to handle network installation from day one \u2014 no IT vendor management required.',
  networkManagement:
    'brought Codi in for managed network services \u2014 monitoring, troubleshooting, and maintenance all covered.',
  cableManagement:
    'brought Codi in to organize and manage all office cabling \u2014 clean runs, labeled, and documented.',
  roomBooking:
    'brought Codi in to deploy and manage a room-booking system \u2014 conference rooms are always visible and reservable.',
  printingSetup:
    'brought Codi in to handle printing setup and ongoing management \u2014 drivers, toner, and maintenance included.',
  interiorDesign:
    'brought Codi in to manage the full interior design and space planning project \u2014 concept through installation.',
  officeMurals:
    'brought Codi in to commission and install a custom office mural \u2014 artist sourcing, permitting, and installation all handled.',
  officePainting:
    'brought Codi in to manage office painting from color selection through final coat \u2014 zero coordination from the team.',
  glassFrosting:
    'brought Codi in to install frosted glass film on conference room partitions \u2014 privacy and brand identity in one project.',
  officeSignage:
    'brought Codi in to design and install office signage \u2014 lobby, wayfinding, and brand wall all coordinated by Codi.',
  careRoom: 'brought Codi in to set up a dedicated care room \u2014 privacy curtain, power, and furniture all handled.',
  furnitureAssembly:
    'brought Codi in to manage furniture assembly and ongoing repairs \u2014 no internal coordination required.',
  layoutChange:
    'brought Codi in to manage a full office layout change \u2014 furniture reconfiguration, cable management, and building access all covered.',
  officePlants: 'brought Codi in to manage office plants and biophilic decor on a recurring maintenance schedule.',
  keylessAccess:
    'brought Codi in to install and manage keyless and digital access \u2014 credentials, lockouts, and access logs all handled.',
  alarmSystems:
    'brought Codi in to install and manage an office alarm system \u2014 monitoring, testing, and vendor coordination all included.',
  securityCameras:
    'brought Codi in to install and manage office security cameras \u2014 placement, wiring, and ongoing monitoring all handled.',
  keypads:
    'brought Codi in to install and manage door keypads across the office \u2014 codes, replacements, and maintenance all covered.',
  lockboxes:
    'brought Codi in to manage key lockboxes for the office \u2014 access coordination and maintenance all handled by Codi.',
  visitorManagement:
    'brought Codi in to set up and manage a visitor management system \u2014 check-in, badges, and compliance all handled.',
  businessInsurance:
    'brought Codi in to source and manage business insurance for the office \u2014 certificates, renewals, and compliance all coordinated.',
  electricalServices:
    'brought Codi in to manage electrical services for the office \u2014 panel work, outlet installs, and licensed vendor coordination all handled.',
  hvac: 'brought Codi in to manage HVAC maintenance and repairs \u2014 filter changes, service calls, and vendor coordination all covered.',
  plumbing:
    'brought Codi in to manage office plumbing \u2014 fixture installs, repairs, and licensed vendor coordination all handled.',
  pestControl:
    'brought Codi in to manage recurring pest control \u2014 inspections, treatments, and vendor scheduling all covered.',
  officeMoving:
    'brought Codi in to manage the office move \u2014 vendor selection, logistics coordination, and move day oversight all handled.',
  mountingInstallation:
    'brought Codi in to manage mounting and installation projects \u2014 TVs, whiteboards, shelving, and artwork all handled by vetted vendors.'
};

export const OFFICE_SEO_SF_LOGISTICS_LOCAL =
  'SoMa and Financial District buildings often require freight elevator scheduling in advance \u2014 Codi manages all logistics.';

export const OFFICE_SEO_SF_PANTRY_DELIVERY_LOCAL =
  'Pantry restocking runs Tuesday/Thursday delivery windows in SoMa offices. Battery St and Market St buildings require freight elevator scheduling 24hrs in advance.';

export const OFFICE_SEO_SF_INTERNET_RISER_LOCAL =
  'Most SoMa and Financial District buildings have fiber in the risers. Codi deploys managed wifi (Meraki or Ubiquiti) and coordinates all riser access with building ops.';

export const OFFICE_SEO_SF_FURNITURE_FREIGHT_LOCAL =
  'SoMa and Financial District buildings with freight-only loading docks require 48-hr coordination with building management. Codi handles all delivery logistics including after-hours installs.';

export const OFFICE_SEO_SF_OFFICE_ASSISTANT_COMPLIANCE_LOCAL =
  'Codi on-site assistants are hired and insured under Codi, deployed a few hours/day to handle packages, pantry tidying, and vendor coordination \u2014 so your team never has to.';

export const OFFICE_SEO_SF_DEEP_CLEANING_LOCAL =
  'SF office buildings require advance coordination for after-hours deep clean access. Codi manages all scheduling and building approvals for deep cleaning vendors.';

export const OFFICE_SEO_SF_CARPET_CLEANING_LOCAL =
  'Carpet cleaning in SoMa and Financial District buildings requires freight elevator booking and insurance documentation. Codi handles all vendor coordination.';

export const OFFICE_SEO_SF_WINDOW_CLEANING_LOCAL =
  'Exterior window cleaning in SoMa high-rises requires rappelling permits and building management approval. Codi coordinates all access and scheduling.';

export const OFFICE_SEO_SF_GLASS_CLEANING_LOCAL =
  'Conference room glass cleaning runs on a weekly schedule in most SF offices. Codi coordinates timing with your team to avoid disruption during meetings.';

export const OFFICE_SEO_SF_WASTE_RECYCLING_LOCAL =
  'San Francisco\u2019s mandatory composting and recycling regulations require careful sorting. Codi manages compliant waste and recycling pickup on a scheduled basis.';

// ─── Phase 4b: Food & Beverage SF locals ──────────────────────────────────────

export const OFFICE_SEO_SF_PANTRY_INVENTORY_LOCAL =
  'Codi tracks pantry consumption across SoMa and Financial District offices using real-time inventory data and auto-generates purchase orders before items run out.';

export const OFFICE_SEO_SF_COFFEE_SERVICE_LOCAL =
  'Codi manages coffee equipment placement, bean sourcing, and monthly maintenance for SF offices. Most SoMa buildings handle equipment deliveries via freight elevator \u2014 Codi coordinates all scheduling.';

export const OFFICE_SEO_SF_WATER_SERVICE_LOCAL =
  'Filtered water dispensers in SF offices are serviced and restocked by Codi on a set schedule. Building freight access in SoMa and the Financial District is coordinated in advance.';

export const OFFICE_SEO_SF_TEAM_LUNCH_LOCAL =
  'Codi sources recurring team lunches from local SF caterers and manages delivery logistics for SoMa and Financial District buildings, including elevator and lobby access.';

export const OFFICE_SEO_SF_BREAKFAST_CATERING_LOCAL =
  'Morning delivery windows in SoMa are tight. Codi coordinates with building management for early access and sources reliable local breakfast vendors for recurring office orders.';

export const OFFICE_SEO_SF_INDIVIDUAL_MEALS_LOCAL =
  'Codi manages daily individual meal programs for SF offices, coordinating with local vendors and handling all delivery logistics including building access and distribution within the office.';

export const OFFICE_SEO_SF_AD_HOC_CATERING_LOCAL =
  'Same-day and next-day catering requests in SF require vendor relationships and building access pre-approval. Codi maintains both so your team can request catering without the logistics headache.';

export const OFFICE_SEO_SF_HAPPY_HOUR_LOCAL =
  'Happy hour beverage delivery in SoMa requires freight elevator scheduling and compliance with building guest policies. Codi manages vendor access and setup so your team just shows up.';

// ─── Phase 4c: Technology & IT SF locals ──────────────────────────────────────

export const OFFICE_SEO_SF_CONFERENCE_ROOM_AV_LOCAL =
  'SoMa and Financial District buildings require advance coordination for A/V vendor access and cable runs. Codi manages all building approvals and IT vendor scheduling.';

export const OFFICE_SEO_SF_NETWORK_INSTALLATION_LOCAL =
  'Most SoMa and Financial District buildings have structured cabling in place. Codi coordinates riser access, ISP provisioning, and managed switch installation with building ops.';

export const OFFICE_SEO_SF_NETWORK_MANAGEMENT_LOCAL =
  'Codi provides ongoing managed network services for SF offices \u2014 remote monitoring, firmware updates, and on-site troubleshooting coordinated through our IT vendor network.';

export const OFFICE_SEO_SF_CABLE_MANAGEMENT_LOCAL =
  'Cable management in SoMa and Financial District offices often involves raised floors and under-desk trunking. Codi coordinates IT vendors familiar with building-specific constraints.';

export const OFFICE_SEO_SF_ROOM_BOOKING_LOCAL =
  'Codi deploys room-booking hardware (tablets, displays) and software for SF offices. Building IT access and network provisioning are coordinated in advance with building management.';

export const OFFICE_SEO_SF_PRINTING_SETUP_LOCAL =
  'Printer installation in SoMa buildings requires freight elevator coordination for larger devices. Codi manages delivery, driver setup, and network configuration for your office printers.';

// ─── Phase 4d: Design & Space SF locals ───────────────────────────────────────

export const OFFICE_SEO_SF_INTERIOR_DESIGN_LOCAL =
  'Interior design projects in SoMa and Financial District offices require building permit coordination and freight access planning. Codi manages the full process from concept approval to final installation.';

export const OFFICE_SEO_SF_OFFICE_MURALS_LOCAL =
  'Mural installation in SF office buildings requires surface preparation coordination and building management approval. Codi manages artist selection, permitting, and installation scheduling.';

export const OFFICE_SEO_SF_OFFICE_PAINTING_LOCAL =
  'Office painting in SoMa buildings requires after-hours access coordination and building management approval for VOC-compliant products. Codi manages vendor scheduling and all building logistics.';

export const OFFICE_SEO_SF_GLASS_FROSTING_LOCAL =
  'Glass frosting installation in SF offices requires minimal building coordination but precise measurement and surface prep. Codi manages the full process from template to final installation.';

export const OFFICE_SEO_SF_OFFICE_SIGNAGE_LOCAL =
  'Signage installation in SoMa and Financial District buildings requires building management approval for wall anchoring. Codi coordinates permits, installation vendors, and final placement.';

export const OFFICE_SEO_SF_CARE_ROOM_LOCAL =
  'Care room setups in SF offices require electrical access coordination and privacy curtain installation. Codi manages all vendor scheduling and building access requirements.';

export const OFFICE_SEO_SF_FURNITURE_ASSEMBLY_LOCAL =
  'Furniture assembly in SoMa buildings requires freight elevator booking for large shipments. Codi coordinates delivery windows, assembly crews, and packaging removal so your team has nothing to manage.';

export const OFFICE_SEO_SF_LAYOUT_CHANGE_LOCAL =
  'Layout changes in SoMa and Financial District offices require freight elevator coordination for furniture moves. Codi manages the full project — furniture reconfiguration, cable rerouting, and building approvals.';

export const OFFICE_SEO_SF_OFFICE_PLANTS_LOCAL =
  'Office plant programs in SF buildings require regular maintenance visits coordinated around office schedules. Codi manages plant sourcing, delivery, and recurring care through a vetted local vendor network.';

// ─── Phase 4e: Security & Access SF locals ────────────────────────────────────

export const OFFICE_SEO_SF_KEYLESS_ACCESS_LOCAL =
  'Keyless access installations in SoMa and Financial District buildings require coordination with building security and property management. Codi manages all permitting, hardware installation, and credential setup.';

export const OFFICE_SEO_SF_ALARM_SYSTEMS_LOCAL =
  'Alarm system installation in SF office buildings requires building management approval and coordination with the monitoring provider. Codi manages vendor selection, installation, and ongoing testing schedules.';

export const OFFICE_SEO_SF_SECURITY_CAMERAS_LOCAL =
  'Security camera installation in SoMa offices requires structured cabling and network coordination. Codi manages vendor access, camera placement approvals, and ongoing footage management.';

export const OFFICE_SEO_SF_KEYPADS_LOCAL =
  'Keypad installations in SoMa and Financial District buildings require coordination with property management for door hardware modifications. Codi manages all building approvals and installer scheduling.';

export const OFFICE_SEO_SF_LOCKBOXES_LOCAL =
  'Lockbox setup in SF office buildings is coordinated with building security to ensure compliant placement. Codi manages all installation and access code distribution for your team.';

export const OFFICE_SEO_SF_VISITOR_MANAGEMENT_LOCAL =
  'Visitor management systems in SoMa offices require network provisioning and building lobby coordination. Codi manages hardware delivery, software setup, and ongoing system administration.';

export const OFFICE_SEO_SF_BUSINESS_INSURANCE_LOCAL =
  'SF office buildings require tenant COI for vendors and lease compliance. Codi coordinates business insurance sourcing, certificate generation, and annual renewals for your office.';

// ─── Phase 4f: Facilities & Maintenance SF locals ─────────────────────────────

export const OFFICE_SEO_SF_ELECTRICAL_SERVICES_LOCAL =
  'Electrical work in SoMa and Financial District offices requires licensed C-10 contractors and building permit coordination. Codi manages vendor selection, permit filing, and inspection scheduling for all electrical projects.';

export const OFFICE_SEO_SF_HVAC_LOCAL =
  'HVAC service in SoMa and Financial District buildings often requires coordination with building engineering for rooftop access and shared system shutdowns. Codi manages all scheduling and licensed HVAC vendor coordination.';

export const OFFICE_SEO_SF_PLUMBING_LOCAL =
  'Plumbing work in SoMa office buildings requires licensed C-36 contractors and building management notification. Codi coordinates vendor scheduling, water shutoff windows, and permit filing for all plumbing projects.';

export const OFFICE_SEO_SF_PEST_CONTROL_LOCAL =
  'Pest control in SF commercial buildings requires licensed PCO operators and documentation for building compliance. Codi manages vendor scheduling, treatment coordination, and compliance records for your office.';

export const OFFICE_SEO_SF_OFFICE_MOVING_LOCAL =
  'Office moves in SoMa and Financial District buildings require freight elevator reservations, COI documentation for movers, and building management notification. Codi manages all move logistics from vendor selection through move-in day.';

export const OFFICE_SEO_SF_MOUNTING_INSTALLATION_LOCAL =
  'Mounting work in SoMa offices requires wall anchoring approval and, for concrete or brick surfaces, coordination with building management. Codi manages installation vendor scheduling and ensures proper anchoring for all mounting projects.';

export type OfficeSeoNycLocalPreset = 'flatiron' | 'manhattanStandard';

const NYC_CLEANING_LOGISTICS_MANHATTAN_STANDARD =
  'NYC buildings require elevator booking for cleaning vendor access \u2014 Codi handles all scheduling. Our primary NYC cleaning vendor is fully insured and familiar with Manhattan building requirements.';

const NYC_CLEANING_LOGISTICS_FLATIRON =
  'NYC buildings require insurance approval for cleaning vendors \u2014 Codi handles the approval process. Our primary NYC cleaning vendors are fully insured and familiar with Manhattan building requirements.';

const NYC_PANTRY_DELIVERY_MANHATTAN_STANDARD =
  'Manhattan buildings have strict freight delivery windows. Codi coordinates elevator bookings for all pantry deliveries \u2014 Tuesday/Thursday mornings are most common.';

const NYC_PANTRY_DELIVERY_FLATIRON =
  'Manhattan buildings have strict delivery windows. Codi coordinates access for all pantry deliveries \u2014 Tuesday/Thursday mornings are most common.';

const NYC_INTERNET_RISER_MANHATTAN_STANDARD =
  'NYC office buildings require tenant COI before IT vendors can access risers. Codi carries vendor insurance and coordinates with building ops on your behalf.';

const NYC_INTERNET_RISER_FLATIRON =
  'NYC office buildings require COI approvals before IT vendors can access your space. Codi coordinate the vendor insurance approval with the building on your behalf and schedules an onsite walkthrough to ensure a tailored quote.';

const NYC_FURNITURE_FREIGHT_MANHATTAN_STANDARD =
  'NYC elevator booking is required for all furniture deliveries \u2014 lead time is typically 3-5 business days. Codi coordinates all move and install logistics.';

const NYC_FURNITURE_FREIGHT_FLATIRON =
  'NYC freight elevator booking is required for all furniture deliveries \u2014 lead time is typically 3-5 business days. Codi coordinates all move and install logistics.';

export const OFFICE_SEO_NYC_OFFICE_ASSISTANT_COMPLIANCE_LOCAL =
  'Manhattan high-rises require all on-site personnel to carry building-specific insurance riders. Codi handles all onboarding and compliance documentation.';

const NYC_DEEP_CLEANING_STANDARD =
  'NYC office buildings require elevator booking and insurance approval for deep cleaning vendors. Codi manages all building coordination and after-hours access scheduling.';

const NYC_CARPET_CLEANING_STANDARD =
  'Carpet cleaning in NYC office buildings requires freight elevator booking and vendor COI approval. Codi handles all building coordination end-to-end.';

const NYC_WINDOW_CLEANING_STANDARD =
  'Exterior window cleaning in Manhattan high-rises requires building management permits and rappelling certification. Codi coordinates all approvals and access.';

const NYC_GLASS_CLEANING_STANDARD =
  'Conference room glass cleaning in NYC offices is scheduled around meeting calendars. Codi coordinates timing with your team to avoid disruption.';

const NYC_WASTE_RECYCLING_STANDARD =
  'NYC building regulations require proper waste stream sorting and scheduled pickup coordination. Codi manages compliant waste and recycling hauling on a set schedule.';

// ─── Phase 4b: Food & Beverage NYC locals ─────────────────────────────────────

const NYC_PANTRY_INVENTORY_STANDARD =
  'Codi tracks pantry consumption in Manhattan offices using real-time inventory data and auto-generates purchase orders. Freight delivery windows in NYC buildings are pre-coordinated by Codi.';

const NYC_COFFEE_SERVICE_STANDARD =
  'Codi manages coffee equipment placement, bean sourcing, and maintenance for NYC offices. Equipment deliveries in Manhattan high-rises require freight elevator booking \u2014 Codi handles all scheduling.';

const NYC_WATER_SERVICE_STANDARD =
  'Filtered water dispensers in NYC offices are serviced by Codi on a recurring schedule. Codi coordinates building freight access and dispenser maintenance with your building management team.';

const NYC_TEAM_LUNCH_STANDARD =
  'Codi sources recurring team lunches from NYC caterers and manages all delivery logistics, including freight elevator booking and lobby check-in for Manhattan buildings.';

const NYC_BREAKFAST_CATERING_STANDARD =
  'Early morning catering deliveries in Manhattan require pre-approved elevator access. Codi coordinates building access and sources reliable local breakfast vendors for your recurring order.';

const NYC_INDIVIDUAL_MEALS_STANDARD =
  'Codi manages daily individual meal programs for NYC offices, handling vendor coordination, delivery windows, and building access compliance for Manhattan locations.';

const NYC_AD_HOC_CATERING_STANDARD =
  'Same-day catering in NYC requires established vendor relationships and building approval. Codi maintains both and handles all delivery logistics so your team can request catering on short notice.';

const NYC_HAPPY_HOUR_STANDARD =
  'Happy hour delivery in Manhattan requires freight elevator booking and compliance with building guest policies. Codi manages vendor access, setup, and cleanup so your team just shows up.';

// ─── Phase 4c: Technology & IT NYC locals ─────────────────────────────────────

const NYC_CONFERENCE_ROOM_AV_STANDARD =
  'NYC office buildings require COI approval before A/V vendors access your space. Codi carries vendor insurance, coordinates building approval, and manages all A/V installation scheduling.';

const NYC_NETWORK_INSTALLATION_STANDARD =
  'Manhattan high-rises require tenant COI and building-ops coordination before network vendors access risers. Codi handles all approvals and coordinates with your ISP for seamless installation.';

const NYC_NETWORK_MANAGEMENT_STANDARD =
  'Codi provides ongoing managed network services for NYC offices \u2014 remote monitoring, on-site troubleshooting, and firmware updates coordinated through our vetted IT vendor network.';

const NYC_CABLE_MANAGEMENT_STANDARD =
  'Cable management in Manhattan office buildings often involves raised floors and strict building IT policies. Codi coordinates IT vendors experienced with NYC high-rise cable environments.';

const NYC_ROOM_BOOKING_STANDARD =
  'Room-booking hardware in NYC offices requires network provisioning and building IT approval. Codi coordinates all installation logistics and manages the ongoing software configuration.';

const NYC_PRINTING_SETUP_STANDARD =
  'Printer delivery in Manhattan buildings requires freight elevator booking and COI approval. Codi coordinates delivery logistics and manages driver setup and network configuration.';

// ─── Phase 4d: Design & Space NYC locals ──────────────────────────────────────

const NYC_INTERIOR_DESIGN_STANDARD =
  'Interior design projects in Manhattan offices require building permit coordination and elevator access planning. Codi manages vendor COI approval, freight scheduling, and building management coordination throughout the project.';

const NYC_OFFICE_MURALS_STANDARD =
  'Mural installation in NYC office buildings requires building management approval and COI compliance for the artist vendor. Codi manages all coordination from surface preparation through final installation.';

const NYC_OFFICE_PAINTING_STANDARD =
  'Office painting in Manhattan high-rises requires after-hours access booking, COI approval, and VOC compliance documentation. Codi manages all building logistics and vendor coordination.';

const NYC_GLASS_FROSTING_STANDARD =
  'Glass frosting installation in NYC offices typically requires minimal building approval but precise scheduling around office hours. Codi manages measurement, material sourcing, and installation coordination.';

const NYC_OFFICE_SIGNAGE_STANDARD =
  'Signage installation in Manhattan office buildings requires building management approval for wall anchoring and drilling. Codi coordinates permits, COI documentation, and installation vendor scheduling.';

const NYC_CARE_ROOM_STANDARD =
  'Care room setups in NYC offices require electrical access coordination and compliance with local privacy requirements. Codi manages all vendor scheduling and building management approvals.';

const NYC_FURNITURE_ASSEMBLY_STANDARD =
  'Furniture assembly in Manhattan buildings requires freight elevator booking and COI approval for assembly crews. Codi coordinates delivery windows, assembly scheduling, and packaging removal.';

const NYC_LAYOUT_CHANGE_STANDARD =
  'Office layout changes in NYC high-rises require freight elevator booking for furniture movement and COI compliance for crews. Codi manages the full project — reconfiguration, cable management, and building coordination.';

const NYC_OFFICE_PLANTS_STANDARD =
  'Office plant programs in NYC buildings require regular maintenance visits coordinated around building access policies. Codi manages plant sourcing, delivery logistics, and ongoing care scheduling through local vendors.';

export function officeSeoNycCleaningLogisticsLocal(preset: OfficeSeoNycLocalPreset): string {
  return preset === 'flatiron' ? NYC_CLEANING_LOGISTICS_FLATIRON : NYC_CLEANING_LOGISTICS_MANHATTAN_STANDARD;
}

export function officeSeoNycPantryDeliveryLocal(preset: OfficeSeoNycLocalPreset): string {
  return preset === 'flatiron' ? NYC_PANTRY_DELIVERY_FLATIRON : NYC_PANTRY_DELIVERY_MANHATTAN_STANDARD;
}

export function officeSeoNycInternetRiserLocal(preset: OfficeSeoNycLocalPreset): string {
  return preset === 'flatiron' ? NYC_INTERNET_RISER_FLATIRON : NYC_INTERNET_RISER_MANHATTAN_STANDARD;
}

export function officeSeoNycFurnitureFreightLocal(preset: OfficeSeoNycLocalPreset): string {
  return preset === 'flatiron' ? NYC_FURNITURE_FREIGHT_FLATIRON : NYC_FURNITURE_FREIGHT_MANHATTAN_STANDARD;
}

export function officeSeoNycDeepCleaningLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_DEEP_CLEANING_STANDARD;
}

export function officeSeoNycCarpetCleaningLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_CARPET_CLEANING_STANDARD;
}

export function officeSeoNycWindowCleaningLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_WINDOW_CLEANING_STANDARD;
}

export function officeSeoNycGlassCleaningLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_GLASS_CLEANING_STANDARD;
}

export function officeSeoNycWasteRecyclingLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_WASTE_RECYCLING_STANDARD;
}

export function officeSeoNycPantryInventoryLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_PANTRY_INVENTORY_STANDARD;
}

export function officeSeoNycCoffeeServiceLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_COFFEE_SERVICE_STANDARD;
}

export function officeSeoNycWaterServiceLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_WATER_SERVICE_STANDARD;
}

export function officeSeoNycTeamLunchLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_TEAM_LUNCH_STANDARD;
}

export function officeSeoNycBreakfastCateringLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_BREAKFAST_CATERING_STANDARD;
}

export function officeSeoNycIndividualMealsLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_INDIVIDUAL_MEALS_STANDARD;
}

export function officeSeoNycAdHocCateringLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_AD_HOC_CATERING_STANDARD;
}

export function officeSeoNycHappyHourLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_HAPPY_HOUR_STANDARD;
}

export function officeSeoNycConferenceRoomAvLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_CONFERENCE_ROOM_AV_STANDARD;
}

export function officeSeoNycNetworkInstallationLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_NETWORK_INSTALLATION_STANDARD;
}

export function officeSeoNycNetworkManagementLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_NETWORK_MANAGEMENT_STANDARD;
}

export function officeSeoNycCableManagementLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_CABLE_MANAGEMENT_STANDARD;
}

export function officeSeoNycRoomBookingLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_ROOM_BOOKING_STANDARD;
}

export function officeSeoNycPrintingSetupLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_PRINTING_SETUP_STANDARD;
}

export function officeSeoNycInteriorDesignLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_INTERIOR_DESIGN_STANDARD;
}

export function officeSeoNycOfficeMuralsLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_OFFICE_MURALS_STANDARD;
}

export function officeSeoNycOfficePaintingLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_OFFICE_PAINTING_STANDARD;
}

export function officeSeoNycGlassFrostingLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_GLASS_FROSTING_STANDARD;
}

export function officeSeoNycOfficeSignageLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_OFFICE_SIGNAGE_STANDARD;
}

export function officeSeoNycCareRoomLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_CARE_ROOM_STANDARD;
}

export function officeSeoNycFurnitureAssemblyLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_FURNITURE_ASSEMBLY_STANDARD;
}

export function officeSeoNycLayoutChangeLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_LAYOUT_CHANGE_STANDARD;
}

export function officeSeoNycOfficePlantsLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_OFFICE_PLANTS_STANDARD;
}

// ─── Phase 4e: Security & Access NYC locals ───────────────────────────────────

const NYC_KEYLESS_ACCESS_STANDARD =
  'Keyless access installations in Manhattan office buildings require building security approval and coordination with property management. Codi manages all permitting, hardware installation, and credential provisioning.';

const NYC_ALARM_SYSTEMS_STANDARD =
  'Alarm system installations in NYC offices require building management approval and monitoring provider coordination. Codi manages vendor selection, installation, and ongoing testing to keep your office compliant.';

const NYC_SECURITY_CAMERAS_STANDARD =
  'Security camera installations in Manhattan offices require structured cabling coordination and building approval. Codi manages vendor access, placement permitting, and ongoing footage management.';

const NYC_KEYPADS_STANDARD =
  'Keypad installations in NYC office buildings require coordination with property management for door hardware modifications. Codi manages all approvals, installer scheduling, and code distribution.';

const NYC_LOCKBOXES_STANDARD =
  'Lockbox setups in Manhattan office buildings are coordinated with building security for compliant placement. Codi manages all installation logistics and access code distribution for your team.';

const NYC_VISITOR_MANAGEMENT_STANDARD =
  'Visitor management system deployments in NYC offices require network provisioning and building lobby coordination. Codi manages hardware delivery, software configuration, and ongoing system administration.';

const NYC_BUSINESS_INSURANCE_STANDARD =
  'Manhattan office buildings require tenant COI for vendors and lease compliance. Codi coordinates business insurance sourcing, certificate generation, and annual renewals for your NYC office.';

export function officeSeoNycKeylessAccessLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_KEYLESS_ACCESS_STANDARD;
}

export function officeSeoNycAlarmSystemsLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_ALARM_SYSTEMS_STANDARD;
}

export function officeSeoNycSecurityCamerasLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_SECURITY_CAMERAS_STANDARD;
}

export function officeSeoNycKeypadsLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_KEYPADS_STANDARD;
}

export function officeSeoNycLockboxesLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_LOCKBOXES_STANDARD;
}

export function officeSeoNycVisitorManagementLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_VISITOR_MANAGEMENT_STANDARD;
}

export function officeSeoNycBusinessInsuranceLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_BUSINESS_INSURANCE_STANDARD;
}

// ─── Phase 4f: Facilities & Maintenance NYC locals ────────────────────────────

const NYC_ELECTRICAL_SERVICES_STANDARD =
  'Electrical work in Manhattan office buildings requires licensed master electricians and DOB permits. Codi manages vendor selection, permit coordination, and inspection scheduling for all electrical projects in your NYC office.';

const NYC_HVAC_STANDARD =
  'HVAC service in Manhattan office buildings requires coordination with building engineering for shared system access and service window scheduling. Codi manages licensed HVAC vendors and all building coordination for your office.';

const NYC_PLUMBING_STANDARD =
  'Plumbing work in NYC office buildings requires licensed plumbers and DEP coordination for water shutoffs. Codi manages vendor scheduling, building notification, and permit filing for all plumbing projects.';

const NYC_PEST_CONTROL_STANDARD =
  'Pest control in NYC commercial buildings requires licensed PCO operators and building management coordination. Codi manages vendor scheduling, treatment coordination, and compliance documentation for your Manhattan office.';

const NYC_OFFICE_MOVING_STANDARD =
  'Office moves in Manhattan require freight elevator reservations, COI documentation, and building management approval. Codi manages all move logistics from vendor selection through move-in day, including building coordination.';

const NYC_MOUNTING_INSTALLATION_STANDARD =
  'Mounting work in NYC office buildings requires wall anchoring approval and coordination with building management for structural constraints. Codi manages installation vendor scheduling and ensures compliant anchoring for all mounting projects.';

export function officeSeoNycElectricalServicesLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_ELECTRICAL_SERVICES_STANDARD;
}

export function officeSeoNycHvacLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_HVAC_STANDARD;
}

export function officeSeoNycPlumbingLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_PLUMBING_STANDARD;
}

export function officeSeoNycPestControlLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_PEST_CONTROL_STANDARD;
}

export function officeSeoNycOfficeMovingLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_OFFICE_MOVING_STANDARD;
}

export function officeSeoNycMountingInstallationLocal(_preset: OfficeSeoNycLocalPreset): string {
  return NYC_MOUNTING_INSTALLATION_STANDARD;
}

/** Phrase inserted into "Across {metro}, {phrase} is among the top 3\u2026" */
export const OFFICE_SEO_METRO_WIDE_DEMAND_SERVICE_PHRASE: Record<OfficeSeoServiceKey, string> = {
  cleaning: 'cleaning',
  pantryRestocking: 'pantry restocking',
  internet: 'internet',
  officeAssistant: 'office assistant',
  officeFurniture: 'office furniture',
  deepCleaning: 'deep cleaning',
  carpetCleaning: 'carpet and upholstery cleaning',
  windowCleaning: 'window cleaning',
  glassConferenceRoom: 'conference room glass cleaning',
  wasteRecycling: 'waste and recycling management',
  pantryInventory: 'pantry inventory and auto-ordering',
  coffeeService: 'office coffee service',
  waterService: 'filtered water service',
  teamLunch: 'recurring team lunch',
  breakfastCatering: 'office breakfast catering',
  individualMeals: 'individual meal delivery',
  adHocCatering: 'office catering',
  happyHour: 'office happy hour',
  conferenceRoomAv: 'conference room A/V setup',
  networkInstallation: 'office network installation',
  networkManagement: 'managed network services',
  cableManagement: 'office cable management',
  roomBooking: 'conference room booking systems',
  printingSetup: 'office printing setup',
  interiorDesign: 'interior design and space planning',
  officeMurals: 'office murals',
  officePainting: 'office painting',
  glassFrosting: 'glass frosting and privacy film',
  officeSignage: 'office signage',
  careRoom: 'care room setup',
  furnitureAssembly: 'furniture assembly and repairs',
  layoutChange: 'office layout changes',
  officePlants: 'office plants and biophilic decor',
  keylessAccess: 'keyless and digital access management',
  alarmSystems: 'office alarm systems',
  securityCameras: 'office security cameras',
  keypads: 'door keypad installation and management',
  lockboxes: 'office lockbox management',
  visitorManagement: 'visitor management systems',
  businessInsurance: 'business insurance coordination',
  electricalServices: 'office electrical services',
  hvac: 'office HVAC maintenance',
  plumbing: 'office plumbing services',
  pestControl: 'office pest control',
  officeMoving: 'office moving coordination',
  mountingInstallation: 'office mounting and installation'
};

/** Metro name as used in demand pullquote (not always same as `metroDisplayName`). */
export type OfficeSeoDemandPullquoteMetro = 'san-francisco' | 'new-york';

const DEMAND_PULLQUOTE_METRO_LABEL: Record<OfficeSeoDemandPullquoteMetro, string> = {
  'san-francisco': 'San Francisco',
  'new-york': 'New York City'
};

export function officeSeoMetroWideDemandPullquoteBold(
  metro: OfficeSeoDemandPullquoteMetro,
  serviceKey: OfficeSeoServiceKey
): string {
  const metroLabel = DEMAND_PULLQUOTE_METRO_LABEL[metro];
  const servicePhrase = OFFICE_SEO_METRO_WIDE_DEMAND_SERVICE_PHRASE[serviceKey];
  return `Across ${metroLabel}, ${servicePhrase} is among the top 3 most requested Codi services \u2014 offices of all sizes rely on Codi to manage it so their teams can stay focused.`;
}
